import { useEffect, useState } from 'react';
import { ArrowRight, Cable, Clock, Layers, Zap } from 'lucide-react';
import { formatUnits, parseUnits } from 'viem';
import { useAccount, useReadContract, useWaitForTransactionReceipt, useWriteContract } from 'wagmi';
import { BRIDGE_ABI, ERC20_ABI } from '../web3/abis';
import { TARGET_CHAIN } from '../web3/config';
import { getDeployment } from '../web3/contracts';
import { isDeferredAtLaunch } from '../web3/launchScope';
import { logTransaction } from '../web3/logTransaction';
import { shortenAddress } from '../web3/useWallet';
import { useBridgeActivity } from '../web3/useBridgeActivity';
import TokenIcon from './TokenIcon';
import TrustBar from './TrustBar';
import TransactionTimeout from './TransactionTimeout';

interface ChainOption {
  id: string;
  label: string;
  short: string;
  color: string;
  live: boolean;
}

// destination chains. Only the Pelagos demo route mints on-chain today; the
// others are shown as upcoming so the multi-chain bridge shape is visible
// without implying transfers that don't happen yet.
const CHAINS: ChainOption[] = [
  { id: 'pelagos', label: 'Pelagos Chain', short: 'PEL', color: '#22d3a8', live: true },
  { id: 'ethereum', label: 'Ethereum', short: 'ETH', color: '#8c8fd6', live: false },
  { id: 'base', label: 'Base', short: 'BASE', color: '#3b82f6', live: false },
  { id: 'solana', label: 'Solana', short: 'SOL', color: '#a3e635', live: false },
  { id: 'sui', label: 'Sui', short: 'SUI', color: '#38bdf8', live: false },
  { id: 'aptos', label: 'Aptos', short: 'APT', color: '#2dd4bf', live: false },
];

function fmt(value: bigint | undefined, digits = 4) {
  if (value === undefined) return '-';
  return Number(formatUnits(value, 18)).toLocaleString('en-US', { maximumFractionDigits: digits });
}

function safeParse(value: string): bigint | null {
  if (!value || Number.isNaN(Number(value)) || Number(value) <= 0) return null;
  try {
    return parseUnits(value as `${number}`, 18);
  } catch {
    return null;
  }
}

export default function BridgeView() {
  const { address, isConnected, chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const onCorrectChain = chainId === TARGET_CHAIN.id;
  const deployment = getDeployment(readChainId);
  const bridge = deployment?.bridge;
  const usdtAddress = deployment?.tokens.USDT;
  const wrappedAddress = bridge?.tokens.USDT?.wrapped;

  const [direction, setDirection] = useState<'lock' | 'unlock'>('lock');
  const [amount, setAmount] = useState('');
  const [destChain, setDestChain] = useState('pelagos');
  const [txError, setTxError] = useState<string | null>(null);
  const amountWei = safeParse(amount);
  const activity = useBridgeActivity();
  const dest = CHAINS.find((c) => c.id === destChain) ?? CHAINS[0];

  const { data: locked, refetch: refetchLocked } = useReadContract({
    address: bridge?.address,
    abi: BRIDGE_ABI,
    functionName: 'lockedBalance',
    args: usdtAddress ? [usdtAddress] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(bridge && usdtAddress), refetchInterval: 15000 },
  });

  const { data: usdtBalance, refetch: refetchUsdt } = useReadContract({
    address: usdtAddress,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(usdtAddress && address) },
  });

  const { data: wrappedBalance, refetch: refetchWrapped } = useReadContract({
    address: wrappedAddress,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(wrappedAddress && address) },
  });

  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: usdtAddress,
    abi: ERC20_ABI,
    functionName: 'allowance',
    args: address && bridge ? [address, bridge.address] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(usdtAddress && address && bridge) },
  });

  const needsApproval =
    direction === 'lock' && amountWei !== null && (allowance === undefined || allowance < amountWei);

  const { writeContract: writeApprove, data: approveHash, isPending: isApprovePending } = useWriteContract();
  const { isLoading: isApproveConfirming, isSuccess: isApproveConfirmed, error: approveReceiptError, refetch: retryApproveReceipt } = useWaitForTransactionReceipt({
    hash: approveHash,
    timeout: 300_000,
  });

  const { writeContract, data: hash, isPending, reset } = useWriteContract();
  const { isLoading: isConfirming, isSuccess: isConfirmed, error: receiptError, refetch: retryReceipt } = useWaitForTransactionReceipt({ hash, timeout: 300_000 });

  useEffect(() => {
    if (isApproveConfirmed) refetchAllowance();
  }, [isApproveConfirmed, refetchAllowance]);

  useEffect(() => {
    if (isConfirmed && hash && address) {
      logTransaction({
        type: direction === 'lock' ? 'bridgeLock' : 'bridgeUnlock',
        hash,
        account: address,
        chainId: readChainId,
        details: { pool: 'BRIDGE-USDT', amount, target: direction === 'lock' ? dest.label : 'BSC Testnet' },
      });
      setAmount('');
      refetchLocked();
      refetchUsdt();
      refetchWrapped();
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfirmed]);

  if (!bridge) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-foam-500">
        Bridge is not deployed on this network.
      </div>
    );
  }

  const handleApprove = () => {
    if (!usdtAddress) return;
    setTxError(null);
    writeApprove(
      { address: usdtAddress, abi: ERC20_ABI, functionName: 'approve', args: [bridge.address, 2n ** 256n - 1n] },
      { onError: (err) => setTxError(err.message) },
    );
  };

  const handleAction = () => {
    if (!amountWei || !usdtAddress) return;
    setTxError(null);
    if (direction === 'lock') {
      writeContract(
        {
          address: bridge.address,
          abi: BRIDGE_ABI,
          functionName: 'lock',
          args: [usdtAddress, amountWei, dest.label],
        },
        { onError: (err) => setTxError(err.message) },
      );
    } else {
      writeContract(
        { address: bridge.address, abi: BRIDGE_ABI, functionName: 'unlock', args: [usdtAddress, amountWei] },
        { onError: (err) => setTxError(err.message) },
      );
    }
  };

  const busy = isApprovePending || isApproveConfirming || isPending || isConfirming;
  const fromBalance = direction === 'lock' ? usdtBalance : wrappedBalance;
  const receiveAmount =
    amountWei !== null ? (direction === 'lock' ? (amountWei * 9990n) / 10000n : amountWei) : null;

  let buttonLabel = '수량을 입력하세요';
  let buttonAction: (() => void) | undefined;
  let buttonDisabled = true;

  if (!isConnected) {
    buttonLabel = '지갑을 연결하세요';
  } else if (!onCorrectChain) {
    buttonLabel = '네트워크를 전환하세요';
  } else if (amountWei) {
    if (needsApproval) {
      buttonLabel = 'Approve USDT';
      buttonAction = handleApprove;
      buttonDisabled = busy;
    } else {
      buttonLabel = direction === 'lock' ? '브릿지 (락업 → pUSDT 발행)' : '브릿지 (pUSDT 소각 → 해제)';
      buttonAction = handleAction;
      buttonDisabled = busy;
    }
  }

  const fromChain = direction === 'lock' ? { label: 'BSC Testnet', short: 'BSC', color: '#f0b90b' } : dest;
  const toChain = direction === 'lock' ? dest : { label: 'BSC Testnet', short: 'BSC', color: '#f0b90b' };
  const fromSymbol = direction === 'lock' ? 'USDT' : 'pUSDT';
  const toSymbol = direction === 'lock' ? 'pUSDT' : 'USDT';

  // On mainnet the demo lock&mint bridge is gated off until a real cross-chain
  // messaging protocol is integrated and audited (see docs/audit-prep.md).
  if (isDeferredAtLaunch('Bridge')) {
    return (
      <div className="flex-1 overflow-y-auto p-6 flex items-center justify-center">
        <div className="max-w-md text-center space-y-4">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-abyss-800 border border-abyss-700 flex items-center justify-center text-tide-400">
            <Cable size={26} />
          </div>
          <h2 className="text-xl font-semibold text-foam-100">Bridge — Coming soon</h2>
          <p className="text-sm text-foam-400 leading-relaxed">
            크로스체인 브릿지는 실제 메시징 프로토콜 연동과 별도 보안 감사를 마친 뒤
            제공됩니다. 그때까지 자산은 스왑·풀에서 안전하게 거래하실 수 있습니다.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      <style>{'@keyframes bridgeFlow{0%{left:0%;opacity:0}10%{opacity:1}90%{opacity:1}100%{left:100%;opacity:0}}'}</style>

      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-abyss-850 border border-abyss-700 flex items-center justify-center text-tide-400">
          <Cable size={20} />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foam-100">Bridge</h2>
          <p className="text-xs text-foam-500 mt-0.5">
            락&민트 메커니즘 데모 — 락업하면 브릿지 토큰이 1:1 발행, 소각하면 해제 (수수료 0.1%)
          </p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <StatCard icon={Layers} label="락업 TVL (USDT)" value={fmt(locked, 2)} />
        <StatCard
          icon={Zap}
          label="24h 전송량"
          value={activity ? activity.volume24h.toLocaleString('en-US', { maximumFractionDigits: 2 }) : '-'}
        />
        <StatCard icon={Clock} label="총 전송 건수" value={activity ? String(activity.totalCount) : '-'} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-3">
          {/* animated cross-chain flow */}
          <div className="p-5 rounded-xl border border-abyss-700 bg-abyss-850">
            <div className="flex items-center justify-between gap-3">
              <ChainNode chain={fromChain} symbol={fromSymbol} label="From" />
              <div className="flex-1 relative h-9 flex items-center">
                <div className="w-full h-0.5 bg-abyss-700 rounded-full relative overflow-hidden">
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-tide-400 shadow-[0_0_8px_2px_rgba(34,211,168,0.6)]"
                    style={{ animation: `bridgeFlow ${busy ? '0.9s' : '2.4s'} linear infinite` }}
                  />
                </div>
                <button
                  onClick={() => {
                    setDirection((d) => (d === 'lock' ? 'unlock' : 'lock'));
                    setAmount('');
                    setTxError(null);
                  }}
                  className="absolute left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-abyss-900 border border-abyss-700 flex items-center justify-center text-foam-300 hover:text-tide-400 hover:rotate-180 transition-all duration-300"
                  aria-label="방향 전환"
                >
                  <ArrowRight size={14} />
                </button>
              </div>
              <ChainNode chain={toChain} symbol={toSymbol} label="To" align="right" />
            </div>
          </div>

          {/* destination chain selector (lock only) */}
          {direction === 'lock' && (
            <div className="p-4 rounded-xl border border-abyss-700 bg-abyss-850">
              <div className="text-[11px] text-foam-500 mb-2">목적지 체인 선택</div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {CHAINS.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => c.live && setDestChain(c.id)}
                    disabled={!c.live}
                    className={`relative flex flex-col items-center gap-1 py-2.5 rounded-lg border transition-colors ${
                      destChain === c.id
                        ? 'border-tide-400 bg-tide-400/10'
                        : c.live
                          ? 'border-abyss-700 bg-abyss-900/50 hover:border-abyss-600'
                          : 'border-abyss-800 bg-abyss-900/30 opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <span
                      className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-abyss-950"
                      style={{ background: c.color }}
                    >
                      {c.short}
                    </span>
                    <span className="text-[10px] text-foam-300">{c.label.split(' ')[0]}</span>
                    {!c.live && (
                      <span className="absolute top-1 right-1 text-[7px] uppercase text-foam-600">곧</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* amount input */}
          <div className="p-4 rounded-xl border border-abyss-700 bg-abyss-850">
            <div className="flex justify-between text-[11px] text-foam-500 mb-1.5">
              <span className="flex items-center gap-1.5">
                <TokenIcon symbol="USDT" size={16} />
                {direction === 'lock' ? 'USDT 수량' : 'pUSDT 수량'}
              </span>
              <button
                onClick={() => fromBalance && setAmount(formatUnits(fromBalance, 18))}
                className="text-tide-400 hover:text-tide-300 font-medium"
              >
                MAX: {fmt(fromBalance, 4)}
              </button>
            </div>
            <input
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
              placeholder="0.0"
              className="w-full bg-transparent text-2xl font-medium text-foam-100 outline-none placeholder:text-foam-700 tabular-nums"
            />
          </div>

          {/* route detail */}
          <div className="p-4 rounded-xl border border-abyss-700 bg-abyss-850 space-y-2 text-[11px]">
            <RouteRow label="라우트" value={`${fromChain.label} → ${toChain.label}`} />
            <RouteRow label="브릿지 수수료" value={direction === 'lock' ? '0.10%' : '무료'} />
            <RouteRow label="예상 소요시간" value="~30초 (데모: 즉시)" />
            <RouteRow
              label="최소 수령량"
              value={receiveAmount !== null ? `${fmt(receiveAmount, 4)} ${toSymbol}` : '-'}
              highlight
            />
          </div>

          <button
            onClick={buttonAction}
            disabled={buttonDisabled || busy}
            className="w-full py-3 rounded-xl font-semibold text-sm transition-colors disabled:bg-abyss-700 disabled:text-foam-500 disabled:cursor-not-allowed bg-tide-500 text-abyss-950 hover:bg-tide-400"
          >
            {busy ? '브릿징 중...' : buttonLabel}
          </button>

          {txError && (
            <div className="px-3 py-2 rounded-md bg-coral-500/15 text-coral-400 text-xs break-words">{txError}</div>
          )}
          <TransactionTimeout attempts={[
            { error: approveReceiptError, hash: approveHash, retry: retryApproveReceipt },
            { error: receiptError, hash, retry: retryReceipt },
          ]} />
          {isConfirmed && !txError && (
            <div className="px-3 py-2 rounded-md bg-tide-400/10 text-tide-400 text-xs">
              {direction === 'lock' ? 'pUSDT가 발행되었습니다.' : 'USDT가 해제되었습니다.'}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-center text-xs">
            <div className="p-3 rounded-lg border border-abyss-700 bg-abyss-850">
              <div className="text-foam-500">내 USDT</div>
              <div className="text-foam-100 font-semibold mt-1 tabular-nums">{fmt(usdtBalance, 4)}</div>
            </div>
            <div className="p-3 rounded-lg border border-abyss-700 bg-abyss-850">
              <div className="text-foam-500">내 pUSDT</div>
              <div className="text-foam-100 font-semibold mt-1 tabular-nums">{fmt(wrappedBalance, 4)}</div>
            </div>
          </div>
        </div>

        <BridgeActivityFeed activity={activity} />
      </div>

      <div className="p-3 rounded-lg border border-abyss-700 bg-abyss-850 text-[11px] text-foam-500 leading-relaxed">
        이 데모는 실제 브릿지의 핵심(락&민트 볼트)을 단일 체인에서 시연합니다. 실제 크로스체인 전송에는
        체인 간 메시징(LayerZero, deBridge 등)과 릴레이어가 추가로 필요하며, 메인넷에서는 검증된 브릿지
        프로토콜을 통합(애그리게이터 방식)하는 것을 권장합니다 — 자체 브릿지 운영은 크립토 역사상 최대
        해킹들이 발생한 영역입니다.
      </div>
      <TrustBar contract={bridge.address} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value }: { icon: typeof Layers; label: string; value: string }) {
  return (
    <div className="p-4 rounded-xl border border-abyss-700 bg-abyss-850">
      <div className="flex items-center gap-1.5 text-[11px] text-foam-500">
        <Icon size={12} />
        {label}
      </div>
      <div className="text-lg font-semibold text-foam-100 mt-1.5 tabular-nums truncate">{value}</div>
    </div>
  );
}

function ChainNode({
  chain,
  symbol,
  label,
  align = 'left',
}: {
  chain: { label: string; short: string; color: string };
  symbol: string;
  label: string;
  align?: 'left' | 'right';
}) {
  return (
    <div className={`flex flex-col ${align === 'right' ? 'items-end text-right' : 'items-start'} gap-1 w-24`}>
      <span className="text-[10px] text-foam-500">{label}</span>
      <span
        className="w-9 h-9 rounded-full flex items-center justify-center text-[10px] font-bold text-abyss-950"
        style={{ background: chain.color }}
      >
        {chain.short}
      </span>
      <span className="text-[11px] font-semibold text-foam-100 truncate max-w-full">{chain.label}</span>
      <span className="text-[10px] text-foam-500">{symbol}</span>
    </div>
  );
}

function RouteRow({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex justify-between">
      <span className="text-foam-500">{label}</span>
      <span className={`tabular-nums ${highlight ? 'text-tide-400 font-semibold' : 'text-foam-300'}`}>{value}</span>
    </div>
  );
}

function BridgeActivityFeed({ activity }: { activity: ReturnType<typeof useBridgeActivity> }) {
  return (
    <div className="p-4 rounded-xl border border-abyss-700 bg-abyss-850 lg:self-start">
      <h3 className="text-sm font-semibold text-foam-100 mb-3 flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-tide-400 animate-pulse" />
        최근 브릿지 활동
      </h3>
      {activity === null ? (
        <div className="text-xs text-foam-500 py-6 text-center">불러오는 중...</div>
      ) : activity.recent.length === 0 ? (
        <div className="text-xs text-foam-500 py-6 text-center">아직 브릿지 전송 기록이 없습니다.</div>
      ) : (
        <div className="space-y-1">
          {activity.recent.map((ev) => (
            <div key={ev.hash} className="flex items-center gap-2.5 py-1.5 border-b border-abyss-700/50 last:border-0">
              <span
                className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 ${
                  ev.type === 'bridgeLock' ? 'bg-tide-400/10 text-tide-400' : 'bg-amber-400/10 text-amber-400'
                }`}
              >
                <ArrowRight size={12} className={ev.type === 'bridgeLock' ? '' : 'rotate-180'} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-foam-100">
                  {ev.type === 'bridgeLock' ? '락 → pUSDT' : 'pUSDT 해제'}
                </div>
                <div className="text-[10px] text-foam-500 truncate">
                  {shortenAddress(ev.account)}
                  {ev.target ? ` · ${ev.target}` : ''}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs tabular-nums text-foam-200">
                  {ev.amount.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                </div>
                <div className="text-[10px] text-foam-600 tabular-nums">
                  {ev.at ? new Date(ev.at).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }) : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
